import { Injectable, Injector } from '@angular/core';
import { LocalStorageService } from './localStorage.service';
import { OrderFlowTraceService } from './telemetry/order-flow-trace.service';
import {
  OrigemFingerprint,
  ResolucaoFingerprint,
  lerRegistro,
  marcarEtapa,
  resolverFingerprint,
} from './fingerprint-id';

/**
 * Identidade do aparelho para o pedido e o cartão. A regra inteira mora em
 * `fingerprint-id.ts`; aqui é só a cola com o Angular: o storage do projeto, o
 * `import()` real e o evento de telemetria.
 *
 * O que mudou em 08/09/2026 e por quê está no cabeçalho de `fingerprint-id.ts`.
 * Em uma linha: o `import('@fingerprintjs/…')` falhava em ~74 devices/semana
 * (bloqueador de conteúdo, Safari iOS) e, sem `catch`, subia ao ErrorHandler
 * como `ChunkLoadError` — que recarregava o cardápio. Agora: um id por device,
 * gravado uma vez; fingerprintjs se der, aleatório se não der; nunca lança.
 *
 * **Compatibilidade:** `fingerprint` continua sendo a propriedade lida por
 * `OrderService` e `CreditCardService`, e `getFingerPrint()` continua
 * devolvendo uma Promise. Duas diferenças a favor: a propriedade já nasce
 * preenchida quando há valor gravado (o pedido não sai mais sem id só porque o
 * pré-aquecimento em idle não terminou), e a Promise NUNCA rejeita.
 *
 * **Sobre marcar a origem no payload:** o id aleatório tem o MESMO formato do
 * `visitorId` (32 hex) de propósito, para nada a jusante mudar. A origem
 * (`fingerprintjs` | `aleatorio`) fica em `origem` aqui e sai na telemetria; se
 * o backend precisar distinguir as duas populações (regra antifraude, dedup de
 * cartão), é um campo novo no payload — combinado antes, não inventado aqui.
 */
@Injectable()
export class FingerprintService {

  /** O id do aparelho. `null` só até a primeira resolução deste device. */
  public fingerprint: string = null;

  /** De onde veio o id: `fingerprintjs` | `aleatorio`. `null` enquanto não há id. */
  public origem: OrigemFingerprint = null;

  /** Uma resolução por vez: chamadas concorrentes esperam a mesma Promise. */
  private emVoo: Promise<string> | null = null;

  constructor(
    private localStorageService: LocalStorageService,
    // `Injector` em vez do serviço direto: a telemetria fica fora da cadeia de
    // construção deste serviço, então nenhum ciclo de DI é possível e um erro
    // ao resolvê-la nunca impede o fingerprint de existir.
    private injector: Injector,
  ) {
    const gravado = lerRegistro(this.localStorageService);
    if (gravado) {
      this.fingerprint = gravado.id;
      this.origem = gravado.origem;
    }
  }

  /**
   * Garante `this.fingerprint` e devolve o id. Nunca rejeita — o pior caso é um
   * id aleatório só em memória, que ainda assim é um id.
   */
  getFingerPrint(): Promise<string> {
    if (this.fingerprint) return Promise.resolve(this.fingerprint);
    if (this.emVoo) return this.emVoo;

    this.emVoo = resolverFingerprint({
      storage: this.localStorageService,
      carregar: () => this.carregarFingerprintJs(),
      // O prazo estourou e o fingerprintjs chegou depois: ele foi gravado para
      // as próximas sessões; esta continua com o aleatório que já entregou.
      aoGravarTardio: r => this.registrar(r),
    }).then(r => {
      this.fingerprint = r.id;
      this.emVoo = null;
      if (r.origem !== 'cache') {
        this.origem = r.origem;
        this.registrar(r);
      } else if (!this.origem) {
        const gravado = lerRegistro(this.localStorageService);
        this.origem = gravado ? gravado.origem : 'fingerprintjs';
      }
      return r.id;
    }, () => {
      // `resolverFingerprint` não rejeita; isto é cinto e suspensório.
      this.emVoo = null;
      return this.fingerprint || '';
    });
    return this.emVoo;
  }

  /**
   * O `import()` real. `webpackChunkName` tira "fingerprintjs" da URL do
   * chunk — é pelo nome que lista de bloqueio e a proteção do Safari casam;
   * o conteúdo continua o mesmo. Cada etapa leva um marcador para a telemetria
   * separar "não baixou" de "baixou e quebrou".
   */
  private carregarFingerprintJs(): Promise<string> {
    const modulo = marcarEtapa('import', import(/* webpackChunkName: "device-signature" */ '@fingerprintjs/fingerprintjs'));
    const agente = marcarEtapa('load', modulo.then(m => m.default.load()));
    const resultado = marcarEtapa('get', agente.then(fp => fp.get()));
    return resultado.then(r => r && r.visitorId);
  }

  /**
   * Um evento por id GERADO (não por leitura de cache): é a razão
   * `aleatorio / (aleatorio + fingerprintjs)` que diz quanto do parque está no
   * fallback, e `motivo` diz por quê. O id em si não viaja.
   */
  private registrar(r: ResolucaoFingerprint): void {
    try {
      const trace = this.injector.get(OrderFlowTraceService);
      trace.emit('ClientFingerprintResolved', {
        out: {
          origem: r.origem,
          motivo: r.motivo || '',
          erro_classe: r.erro_classe || '',
          ms: r.ms,
          persistido: r.persistido,
        },
      }, 'FingerprintService.getFingerPrint');
    } catch (e) {
      // Telemetria nunca pode custar o fingerprint.
    }
  }
}
